import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, TextField, Button, Alert, Snackbar, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Chip, Stack, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { fetchAuthSession } from 'aws-amplify/auth';
import { createUser, updateUser, deleteUser, getCompanies, getUsers } from '../../utils/clientUtils';
import CompanyTreePicker from '../../components/CompanyTreePicker';

export default function Permissions() {
  const { t } = useTranslation();
  const [isAdmin, setIsAdmin] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', company: '', companyPath: '' });
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editDialog, setEditDialog] = useState({ open: false, user: null, companyPath: '' });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const loadUsers = () => {
    setUsersLoading(true);
    getUsers().then(data => {
      setUsers(data);
      setUsersLoading(false);
    });
  };

  useEffect(() => {
    fetchAuthSession().then(session => {
      const groups = session.tokens?.idToken?.payload?.['cognito:groups'] ?? [];
      const admin = groups.includes('user_management');
      setIsAdmin(admin);
      if (admin) {
        getCompanies().then(setCompanies);
        loadUsers();
      }
    });
  }, []);

  const handleCompanyChange = (path) => {
    const selected = companies.find(c => c.path === path);
    if (selected) {
      setForm(prev => ({ ...prev, company: selected.companyID, companyPath: selected.path }));
    }
  };

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const result = await createUser(form);
    setSubmitting(false);
    if (result && result.message) {
      setSnackbar({ open: true, message: t('permissions.userCreatedSuccess', { email: form.email }), severity: 'success' });
      setForm({ email: '', name: '', company: '', companyPath: '' });
      loadUsers();
    } else {
      const errMsg = result?.error || t('permissions.failedToCreateUser');
      setSnackbar({ open: true, message: errMsg, severity: 'error' });
    }
  };

  const handleEditOpen = (user) => {
    setEditDialog({ open: true, user, companyPath: user.company_path || '' });
  };

  const handleEditCompanyChange = (path) => {
    setEditDialog(prev => ({ ...prev, companyPath: path }));
  };

  const handleEditSubmit = async () => {
    setEditSubmitting(true);
    const result = await updateUser({ username: editDialog.user.username, companyPath: editDialog.companyPath });
    setEditSubmitting(false);
    if (result && result.message) {
      setSnackbar({ open: true, message: t('permissions.userUpdatedSuccess', { email: editDialog.user.email }), severity: 'success' });
      setEditDialog({ open: false, user: null, companyPath: '' });
      loadUsers();
    } else {
      const errMsg = result?.error || t('permissions.failedToUpdateUser');
      setSnackbar({ open: true, message: errMsg, severity: 'error' });
    }
  };

  const handleDeleteOpen = (user) => {
    setDeleteDialog({ open: true, user });
  };

  const handleDeleteSubmit = async () => {
    setDeleteSubmitting(true);
    const result = await deleteUser(deleteDialog.user.username);
    setDeleteSubmitting(false);
    if (result && result.message) {
      setSnackbar({ open: true, message: t('permissions.userDeletedSuccess', { email: deleteDialog.user.email }), severity: 'success' });
      setDeleteDialog({ open: false, user: null });
      loadUsers();
    } else {
      const errMsg = result?.error || t('permissions.failedToDeleteUser');
      setSnackbar({ open: true, message: errMsg, severity: 'error' });
    }
  };

  const statusColor = (status) => {
    if (status === 'CONFIRMED') return 'success';
    if (status === 'FORCE_CHANGE_PASSWORD') return 'warning';
    return 'default';
  };

  return (
    <Box>
      <Typography variant='h2' sx={{ color: 'primary.main', textAlign: 'left' }}>{t('permissions.title')}</Typography>

      {isAdmin === null && <CircularProgress sx={{ mt: 2 }} />}

      {isAdmin === false && (
        <Alert severity="warning" sx={{ mt: 2 }}>{t('permissions.noPermission')}</Alert>
      )}

      {isAdmin === true && (
        <>
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>{t('permissions.currentUsers')}</Typography>
          {usersLoading ? (
            <CircularProgress />
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('permissions.email')}</TableCell>
                  <TableCell>{t('permissions.name')}</TableCell>
                  <TableCell>{t('permissions.company')}</TableCell>
                  <TableCell>{t('permissions.companyPath')}</TableCell>
                  <TableCell>{t('permissions.groups')}</TableCell>
                  <TableCell>{t('permissions.status')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.username}>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>{u.company}</TableCell>
                    <TableCell>{u.company_path}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {(u.groups ?? []).map(g => <Chip key={g} label={g} size="small" />)}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip label={u.status} color={statusColor(u.status)} size="small" />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={t('permissions.editCompany')}>
                        <IconButton size="small" onClick={() => handleEditOpen(u)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('permissions.deleteUser')}>
                        <IconButton size="small" color="error" onClick={() => handleDeleteOpen(u)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </Box>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4, maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">{t('permissions.createUser')}</Typography>
            <TextField
              label={t('permissions.email')}
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label={t('permissions.name')}
              name="name"
              value={form.name}
              onChange={handleChange}
              fullWidth
            />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>{t('permissions.company')} *</Typography>
              <CompanyTreePicker companies={companies} value={form.companyPath} onChange={handleCompanyChange} />
            </Box>
            <Button type="submit" variant="contained" disabled={submitting || !form.company}>
              {submitting ? <CircularProgress size={22} /> : t('permissions.createUser')}
            </Button>
          </Box>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={deleteDialog.open} onClose={() => !deleteSubmitting && setDeleteDialog({ open: false, user: null })} maxWidth="xs" fullWidth>
        <DialogTitle>{t('permissions.deleteUserDialog', { email: deleteDialog.user?.email })}</DialogTitle>
        <DialogContent>
          <Typography>{t('permissions.deleteUserConfirm', { email: deleteDialog.user?.email })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, user: null })} disabled={deleteSubmitting}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteSubmit} variant="contained" color="error" disabled={deleteSubmitting}>
            {deleteSubmitting ? <CircularProgress size={20} /> : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialog.open} onClose={() => !editSubmitting && setEditDialog({ open: false, user: null, companyPath: '' })} fullWidth maxWidth="xs">
        <DialogTitle>{t('permissions.editCompanyDialog', { email: editDialog.user?.email })}</DialogTitle>
        <DialogContent>
          <CompanyTreePicker
            companies={companies}
            value={editDialog.companyPath}
            onChange={handleEditCompanyChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, user: null, companyPath: '' })} disabled={editSubmitting}>{t('common.cancel')}</Button>
          <Button onClick={handleEditSubmit} variant="contained" disabled={editSubmitting || !editDialog.companyPath}>
            {editSubmitting ? <CircularProgress size={20} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
